import React, { useEffect } from "react";
import { CONTEXT_MENU_BOTTOM_PADDING_PX } from "pages/flyer/builder/drawer/constants/constants";

const CONTEXT_MENU_SELECTOR = '.tlui-menu[data-testid="context-menu"]';
const RADIX_POPPER_WRAPPER_ATTR = "data-radix-popper-content-wrapper";
const CLAMP_PENDING_ATTR = "data-context-menu-clamp-pending";
const CLAMP_APPLIED_ATTR = "data-context-menu-clamp-applied";
const MAX_BOTTOM = () => window.innerHeight - CONTEXT_MENU_BOTTOM_PADDING_PX;

function getWrapper(menu: HTMLElement): HTMLElement {
  return (menu.closest(`[${RADIX_POPPER_WRAPPER_ATTR}]`) as HTMLElement | null) || menu;
}

function needsClamp(wrapper: HTMLElement): boolean {
  const rect = wrapper.getBoundingClientRect();
  const maxBottom = MAX_BOTTOM();
  return rect.bottom > maxBottom;
}

function clampContextMenuBottom(wrapper: HTMLElement) {
  const rect = wrapper.getBoundingClientRect();
  const maxBottom = MAX_BOTTOM();
  if (rect.bottom <= maxBottom) return;
  const newTop = Math.max(0, maxBottom - rect.height);
  wrapper.style.setProperty("transform", `translate(${rect.left}px, ${newTop}px)`);
}

function hideThenClampThenShow(wrapper: HTMLElement) {
  // Always hide first to prevent flash
  wrapper.style.setProperty("visibility", "hidden");
  wrapper.setAttribute(CLAMP_PENDING_ATTR, "true");

  requestAnimationFrame(() => {
    // Check if clamp is needed after browser has calculated final position
    if (needsClamp(wrapper)) {
      clampContextMenuBottom(wrapper);
    }

    requestAnimationFrame(() => {
      wrapper.style.removeProperty("visibility");
      wrapper.removeAttribute(CLAMP_PENDING_ATTR);
      wrapper.setAttribute(CLAMP_APPLIED_ATTR, "true");
    });
  });
}

/**
 * When the tldraw context menu appears, hides it until the clamp has run so it never
 * flashes through the BottomToolPanel. Applies the transform to the Radix Popper
 * wrapper so the menu stays above the panel.
 */
export function ContextMenuClamp() {
  useEffect(() => {
    let lastWrapper: HTMLElement | null = null;

    const observer = new MutationObserver(() => {
      const menu = document.querySelector(CONTEXT_MENU_SELECTOR) as HTMLElement | null;

      // If menu disappeared, clean up the last wrapper
      if (!menu && lastWrapper) {
        lastWrapper.removeAttribute(CLAMP_APPLIED_ATTR);
        lastWrapper.removeAttribute(CLAMP_PENDING_ATTR);
        lastWrapper = null;
        return;
      }

      if (!menu) return;

      const wrapper = getWrapper(menu);
      lastWrapper = wrapper;

      // Skip if already being clamped or if already clamped
      if (wrapper.hasAttribute(CLAMP_PENDING_ATTR) || wrapper.hasAttribute(CLAMP_APPLIED_ATTR)) return;
      hideThenClampThenShow(wrapper);
    });

    const onResize = () => {
      const menu = document.querySelector(CONTEXT_MENU_SELECTOR) as HTMLElement | null;
      if (!menu) return;
      const wrapper = getWrapper(menu);
      // Remove the applied flag on resize so it can re-clamp if needed
      wrapper.removeAttribute(CLAMP_APPLIED_ATTR);
      clampContextMenuBottom(wrapper);
      wrapper.setAttribute(CLAMP_APPLIED_ATTR, "true");
    };

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return null;
}
